<template>
    <div class="compare-flow-node" :class="wrapperClasses" @click="$emit('node-click', { id, data, diffState })">
        <!-- Diff State Badge -->
        <div v-if="diffState && diffState !== 'unchanged'" class="diff-badge" :class="'diff-badge-' + diffState">
            <v-icon size="14">{{ badgeIcon }}</v-icon>
            <span v-if="changeCount > 0" class="badge-count">{{ changeCount }}</span>
        </div>

        <!-- Actual FlowNode -->
        <FlowNode :id="id" :data="data" />
    </div>
</template>

<script>
import FlowNode from '@/components/FlowComponents/FlowNode.vue';

export default {
    name: 'CompareFlowNode',
    components: { FlowNode },
    inject: {
        /** Map<matchKey, { state, changeCount }> provided by FlowCompare */
        nodeDiffStates: {
            default: () => new Map()
        },
    },
    props: {
        id: { type: String, required: true },
        data: { type: Object, required: true, default: () => ({ type: 'default', label: '' }) },
    },
    emits: ['node-click'],
    computed: {
        matchKey() {
            return this.data?._matchKey || this.id;
        },
        /** Safely unwrap the injected nodeDiffStates (may be a computed ref or a raw Map) */
        resolvedDiffStates() {
            const raw = this.nodeDiffStates;
            if (!raw) return null;
            // If Vue auto-unwrapped the computed ref, it's already a Map
            if (typeof raw.get === 'function') return raw;
            // If it's still a ref/computed, access .value
            if (raw.value && typeof raw.value.get === 'function') return raw.value;
            return null;
        },
        diffInfo() {
            const map = this.resolvedDiffStates;
            if (!map) return null;
            return map.get(this.matchKey) || null;
        },
        diffState() {
            return this.diffInfo?.state || 'unchanged';
        },
        changeCount() {
            return this.diffInfo?.changeCount || 0;
        },
        wrapperClasses() {
            const cls = [];
            if (this.diffState) {
                cls.push('diff-state-' + this.diffState);
            }
            return cls;
        },
        badgeIcon() {
            const icons = {
                added: 'mdi-plus-circle',
                removed: 'mdi-minus-circle',
                modified: 'mdi-pencil-circle',
            };
            return icons[this.diffState] || 'mdi-help-circle';
        }
    }
};
</script>

<style scoped>
.compare-flow-node {
    position: relative;
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 12px;
}

/* Diff state borders and backgrounds */
.diff-state-added {
    outline: 3px solid #22c55e;
    outline-offset: 4px;
    border-radius: 12px;
}

.diff-state-removed {
    outline: 3px solid #ef4444;
    outline-offset: 4px;
    border-radius: 12px;
    opacity: 0.6;
}

.diff-state-modified {
    outline: 3px solid #f59e0b;
    outline-offset: 4px;
    border-radius: 12px;
}

.diff-state-unchanged {
    opacity: 0.5;
    filter: grayscale(30%);
}

/* Hover effect for modified nodes */
.diff-state-modified:hover,
.diff-state-added:hover,
.diff-state-removed:hover {
    outline-width: 4px;
    transform: scale(1.02);
}

.diff-state-unchanged:hover {
    opacity: 0.7;
}

/* Pulse animation for modified */
.diff-state-modified::after {
    content: '';
    position: absolute;
    top: -6px;
    left: -6px;
    right: -6px;
    bottom: -6px;
    border-radius: 14px;
    border: 2px solid #f59e0b;
    animation: pulseBorder 2s ease-in-out infinite;
    pointer-events: none;
}

@keyframes pulseBorder {
    0%, 100% { opacity: 0; }
    50% { opacity: 0.5; }
}

/* Badge */
.diff-badge {
    position: absolute;
    top: -12px;
    right: -12px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 0.7rem;
    font-weight: 600;
    color: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.diff-badge-added {
    background: #22c55e;
}

.diff-badge-removed {
    background: #ef4444;
}

.diff-badge-modified {
    background: #f59e0b;
}

.badge-count {
    font-size: 0.65rem;
}
</style>
